import { parse } from 'node-html-parser';

import { getInternalHostnames } from 'lib/utils';

import type { HTMLElement as ParsedHTMLElement } from 'node-html-parser';

/**
 *
 * @param href string
 * @returns valid hostname or empty string
 */
export const getHostname = (href: string) => {
  try {
    const url = new URL(href);
    return url.hostname.toLowerCase();
  } catch (error) {
    return '';
  }
};

const REQUIRED_EXTERNAL_REL = ['noopener', 'noreferrer'];

/** Sets `target="_blank"` and the `rel` that must accompany it, keeping any existing tokens. */
const setExternalTarget = (link: ParsedHTMLElement): void => {
  link.setAttribute('target', '_blank');

  const existing = (link.getAttribute('rel') ?? '')
    .split(/\s+/)
    .filter(Boolean);
  const merged = [
    ...existing,
    ...REQUIRED_EXTERNAL_REL.filter((token) => !existing.includes(token)),
  ];

  link.setAttribute('rel', merged.join(' '));
};

/**
 * @param textHtml string input
 * @returns parsed string
 * @description Internal links are left exactly as they are, so they keep
 * navigating in the current tab. Internal means the hostname matches
 * `getInternalHostnames()` in `lib/utils.ts` — the `VITE_KLUE_V1_BASEURL`
 * host and its `v2.` / `help.` subdomains. Every other link gets
 * `target="_blank"` plus the `rel="noopener noreferrer"` that has to
 * accompany it, so the opened page cannot reach back through
 * `window.opener`. Any `rel` the author already set is kept.
 * @example an internal link is returned unchanged:
 * <a href="https://app.klue.com/profile/123" title="Link title">Internal link</a>
 * stays <a href="https://app.klue.com/profile/123" title="Link title">Internal link</a>
 * @example any other link opens in a new tab, severed from this one:
 * <a href="https://example.com/post" title="Link title">External link</a>
 * becomes <a href="https://example.com/post" title="Link title" target="_blank" rel="noopener noreferrer">External link</a>
 */
export function transformLinks(textHtml: string) {
  const document = parse(textHtml, { parseNoneClosedTags: true });
  const links = document.querySelectorAll('a');

  links.forEach((link) => {
    const hrefValue = link.getAttribute('href');

    if (!hrefValue) {
      return;
    }

    const hostname = getHostname(hrefValue);

    if (!hostname) {
      return;
    }
    const isKlueLink = hostname.includes('klue');

    if (!isKlueLink) {
      setExternalTarget(link);
    }

    const isInternalLink = getInternalHostnames().some((part) => {
      return hostname.startsWith(part);
    });

    if (isInternalLink) {
      return;
    }

    setExternalTarget(link);
  });

  return document.toString();
}

const YOUTUBE_URL_REGEX =
  /(?<!=['"])(https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{11})(?=[&#?\s"'<.,);:]|$)/gi;

/**
 * @param textHtml string input
 * @returns parsed string
 */
export function transformYouTubeUrls(textHtml: string) {
  if (!textHtml || typeof textHtml !== 'string') {
    return textHtml;
  }

  // convert Youtube URLs to inline video
  return textHtml.replace(
    YOUTUBE_URL_REGEX,
    (
      match: string,
      _protocol: string,
      ytid: string,
      offset: number,
      original: string,
    ) => {
      // Skip if inside an HTML attribute value (e.g. href="...")
      const before = original.slice(0, offset);
      if (/(?:href|src)\s*=\s*["'][^"']*$/i.test(before)) {
        return match;
      }

      return `<span class="fr-video fr-deletable fr-rv fr-fvc fr-dvb">
         <iframe width="640" height="360"
           src="https://www.youtube-nocookie.com/embed/${ytid}?modestbranding=1&showinfo=0&rel=0&controls=2&color=white&wmode=opaque"
           loading="lazy"
           allowtransparency="true"
           frameborder="0"
           allowfullscreen></iframe>
       </span>`;
    },
  );
}

/**
 *
 * @param htmlString input html content
 * @param functions array of transform functions to be applied
 * @example const parsed = applyDecorators(item, [func1, func2, func3]);
 *
 */
export const applyDecorators = (
  htmlString: string,
  functions: Array<(data: string) => string>,
): string => {
  return functions.reduce((acc, func) => {
    return func(acc);
  }, htmlString);
};
